import React from 'react';
import { FiGlobe, FiShield, FiFileText, FiCheckCircle } from 'react-icons/fi';
import SEO from '../components/SEO/SEO';

const PrivacyPolicy: React.FC = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy - travelbaba786',
    url: 'https://travelbaba786.com/privacy',
    description: 'Privacy Policy for travelbaba786. Learn how we protect and handle your personal information.',
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    publisher: {
      '@type': 'Organization',
      name: 'travelbaba786',
      url: 'https://travelbaba786.com',
    },
  };

  return (
    <>
      <SEO
        title="Privacy Policy - travelbaba786"
        description="Privacy Policy for travelbaba786. Learn how we protect and handle your personal information. We are committed to protecting your privacy and ensuring the security of your personal information."
        keywords="privacy policy, data protection, privacy, user privacy, data security, travelers api privacy"
        url="https://travelbaba786.com/privacy"
        structuredData={structuredData}
      />
      <div className="max-w-5xl mx-auto">
        {/* Header */}
      <div className="text-center mb-16">
        <div className="mb-8 group">
          <div className="relative inline-block">
            <div className="absolute inset-0 rounded-full blur-2xl scale-150 bg-blue-500/20"></div>
            <div className="relative w-20 h-20 rounded-full flex items-center justify-center border-2 bg-white/50 dark:bg-gray-800/50 border-blue-300/30 dark:border-blue-500/30 backdrop-blur-sm group-hover:scale-110 transition-transform duration-500">
              <FiShield className="w-10 h-10 text-blue-500 dark:text-blue-400" />
            </div>
          </div>
        </div>
        <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-gray-900 via-blue-600 to-blue-700 dark:from-white dark:via-blue-100 dark:to-blue-200 bg-clip-text text-transparent leading-none pb-3">
          Privacy Policy
        </h1>
        <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-600 dark:text-gray-300">
          How we protect and handle your personal information
        </p>
      </div>

      {/* Privacy Policy Sections */}
      <div className="space-y-10">
        
        {/* Introduction */}
        <div className="relative rounded-3xl p-10 mb-12 shadow-lg border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
              <FiFileText className="w-6 h-6" />
            </div>
            <h2 className="text-[28px] font-bold text-gray-800 dark:text-white">
              Our Commitment to Your Privacy
            </h2>
          </div>

          <div className="space-y-4 text-[15px] leading-relaxed text-gray-700 dark:text-gray-300 pl-16">
            <p>
              We at travelbaba786 are committed to protecting your privacy and ensuring the security 
              of your personal information. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information.
            </p>
            <p>
              By using our service, you agree to the terms outlined in this Privacy Policy.
            </p>
          </div>
        </div>

        {/* Information We Collect */}
        <div className="group relative rounded-3xl p-10 transition-all duration-500 hover:scale-105 shadow-lg border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
              <FiShield className="w-8 h-8" />
            </div>
            <h2 className="text-[28px] font-bold text-gray-800 dark:text-white">
              Information We Collect
            </h2>
          </div>

          <div className="space-y-5 text-[16px] pl-16 leading-relaxed text-gray-700 dark:text-gray-300">
            <p>
              We collect minimal information necessary to provide our services:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Usage data and analytics to improve our service</li>
              <li>Anonymous statistical information about page views and interactions</li>
              <li>Technical information such as browser type and device information</li>
            </ul>
          </div>
        </div>

        {/* How We Use Information */}
        <div className="group relative rounded-3xl p-10 transition-all duration-500 hover:scale-105 shadow-lg border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
              <FiCheckCircle className="w-7 h-7" />
            </div>
            <h2 className="text-[28px] font-bold text-gray-800 dark:text-white">
              How We Use Your Information
            </h2>
          </div>

          <div className="space-y-5 text-[16px] pl-16 leading-relaxed text-gray-700 dark:text-gray-300">
            <p>
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Provide and improve our travel information services</li>
              <li>Analyze usage patterns to enhance user experience</li>
              <li>Ensure the security and proper functioning of our platform</li>
              <li>Comply with legal obligations</li>
            </ul>
          </div>
        </div>

        {/* Data Protection */}
        <div className="group relative rounded-3xl p-10 transition-all duration-500 hover:scale-105 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <div className="relative flex items-center space-x-4 mb-8">
            <div className="p-3 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform duration-500">
              <FiShield className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Data Security
            </h2>
          </div>
          <div className="space-y-6 text-lg pl-16 leading-relaxed text-gray-700 dark:text-gray-300">
            <p>
              We implement appropriate technical and organizational measures to protect your 
              information against unauthorized access, alteration, disclosure, or destruction.
            </p>
            <p>
              While we strive to use commercially acceptable means to protect your information, 
              no method of transmission over the Internet is 100% secure.
            </p>
            <p>
              We do not store personally identifiable information unless absolutely necessary 
              for the provision of our services.
            </p>
          </div>
        </div>

        {/* Third-Party Services */}
        <div className="group relative rounded-3xl p-10 transition-all duration-500 hover:scale-105 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

          <div className="relative">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-2xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                <FiGlobe className="w-8 h-8" />
              </div>
              <h2 className="text-[28px] font-bold text-gray-800 dark:text-white">Third-Party Services</h2>
            </div>

            <div className="space-y-5 text-[16px] leading-relaxed pl-16 text-gray-700 dark:text-gray-300">
              <p>
                We may use third-party services for analytics and to improve our platform. 
                These services have their own privacy policies governing the handling of information.
              </p>
              <p>
                We do not sell, trade, or transfer your personal information to third parties 
                for marketing purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Cookies */}
        <div className="relative rounded-3xl p-10 overflow-hidden group bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 backdrop-blur-sm">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>

          <div className="relative">
            <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
              Cookies and Tracking
            </h2>
            <div className="text-lg leading-relaxed space-y-4 text-gray-700 dark:text-gray-300">
              <p>
                We may use cookies and similar tracking technologies to enhance your experience 
                on our platform and to analyze usage patterns.
              </p>
              <p>
                You have the option to disable cookies through your browser settings, though 
                this may affect some functionality of our service.
              </p>
            </div>
          </div>
        </div>

        {/* Your Rights */}
        <div className="relative rounded-3xl p-10 shadow-lg border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 rounded-2xl bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
              <FiCheckCircle className="w-8 h-8" />
            </div>
            <h2 className="text-[28px] font-bold text-gray-800 dark:text-white">
              Your Privacy Rights
            </h2>
          </div>

          <div className="space-y-5 text-[16px] pl-16 leading-relaxed text-gray-700 dark:text-gray-300">
            <p>
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of certain data processing activities</li>
            </ul>
          </div>
        </div>

        {/* Contact */}
        <div className="relative rounded-3xl p-10 mb-12 shadow-lg border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">
            Contact Us
          </h2>
          <div className="text-lg leading-relaxed space-y-4 text-gray-700 dark:text-gray-300">
            <p>
              If you have questions about this Privacy Policy or how we handle your information, 
              please contact us through our official channels.
            </p>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;

